import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createWalletClient, custom, getAddress, numberToHex, type WalletClient } from 'viem';
import { robinhoodTestnet } from '../../shared/chains';
import { MARKET } from '../../shared/market.generated';
import type { Address } from '../../shared/types';
import { EMBEDDED_RDNS, getWallets, requestProviders, subscribeWallets, type Eip1193Provider, type WalletOption } from './eip6963';

const LAST_WALLET_KEY = 'rwalend:last-wallet';
const CHAIN_HEX = numberToHex(MARKET.chainId);

interface WalletContextValue {
  wallets: WalletOption[];
  status: 'disconnected' | 'connecting' | 'connected';
  address: Address | null;
  chainId: number | null;
  /** Shown under the address in the account menu: the extension's name, or the account signed in to the embedded wallet. */
  walletLabel: string | null;
  /** 'embedded' for a Privy-held wallet (no extension, no token list), 'injected' for a browser extension. */
  walletKind: WalletOption['kind'] | null;
  wrongNetwork: boolean;
  /** True once the silent reconnect to the previously used wallet has been attempted (or was not applicable). */
  settled: boolean;
  connect(option: WalletOption): Promise<void>;
  disconnect(): void;
  switchNetwork(): Promise<void>;
  watchAsset(token: { address: Address; symbol: string; decimals: number }): Promise<void>;
  /** A viem wallet client bound to the connected account, or null. */
  client: WalletClient | null;
}

const Ctx = createContext<WalletContextValue | null>(null);
const noAccount: NonNullable<WalletOption['account']> = { get: () => null, subscribe: () => () => undefined };
const safeStorage = {
  get: (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string | null): void => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* private mode */ } },
};

export function WalletProvider({ children }: { children: ReactNode }) {
  const wallets = useSyncExternalStore(subscribeWallets, getWallets);
  const [active, setActive] = useState<WalletOption | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [status, setStatus] = useState<WalletContextValue['status']>('disconnected');
  const [settled, setSettled] = useState(false);
  const triedReconnect = useRef(false);
  const accountStore = active?.account ?? noAccount;
  const account = useSyncExternalStore(accountStore.subscribe, accountStore.get);

  useEffect(() => {
    requestProviders();
    // No remembered wallet: nothing to wait for. Otherwise give the extension a moment to announce itself; the
    // embedded wallet also has to download its SDK and restore the Privy session, which takes longer.
    const last = safeStorage.get(LAST_WALLET_KEY);
    if (!last) { setSettled(true); return; }
    const t = setTimeout(() => setSettled(true), last === EMBEDDED_RDNS ? 8000 : 1500);
    return () => clearTimeout(t);
  }, []);

  const reset = useCallback(() => { setActive(null); setAddress(null); setChainId(null); setStatus('disconnected'); }, []);

  const attach = useCallback(async (option: WalletOption, prompt: boolean) => {
    const accounts = (await option.provider.request({ method: prompt ? 'eth_requestAccounts' : 'eth_accounts' })) as string[];
    if (!accounts?.length) return false;
    const chainHex = (await option.provider.request({ method: 'eth_chainId' })) as string;
    setActive(option);
    setAddress(getAddress(accounts[0]!));
    setChainId(Number(chainHex));
    setStatus('connected');
    safeStorage.set(LAST_WALLET_KEY, option.rdns);
    return true;
  }, []);

  const switchNetworkOn = useCallback(async (provider: Eip1193Provider) => {
    try {
      await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: CHAIN_HEX }] });
    } catch (e) {
      const code = (e as { code?: number; data?: { originalError?: { code?: number } } }).code ?? (e as { data?: { originalError?: { code?: number } } }).data?.originalError?.code;
      if (code !== 4902) throw e;
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [{ chainId: CHAIN_HEX, chainName: robinhoodTestnet.name, nativeCurrency: robinhoodTestnet.nativeCurrency, rpcUrls: [MARKET.rpc], blockExplorerUrls: [MARKET.explorer] }],
      });
    }
    setChainId(Number((await provider.request({ method: 'eth_chainId' })) as string));
  }, []);

  const connect = useCallback(async (option: WalletOption) => {
    setStatus('connecting');
    try {
      if (!(await attach(option, true))) { reset(); return; }
      const chainHex = (await option.provider.request({ method: 'eth_chainId' })) as string;
      if (Number(chainHex) !== MARKET.chainId) await switchNetworkOn(option.provider).catch(() => undefined); // the banner offers a retry
    } catch (e) {
      reset();
      throw e;
    }
  }, [attach, reset, switchNetworkOn]);

  // Silent reconnect to the wallet used last time (eth_accounts never opens a prompt).
  useEffect(() => {
    if (triedReconnect.current || active) return;
    const last = safeStorage.get(LAST_WALLET_KEY);
    const option = last ? wallets.find((w) => w.rdns === last) : undefined;
    if (!option) return;
    triedReconnect.current = true;
    attach(option, false).catch(() => undefined).finally(() => setSettled(true));
  }, [wallets, active, attach]);

  useEffect(() => {
    const p = active?.provider;
    if (!p?.on) return;
    const onAccounts = ((accounts: string[]) => { if (accounts?.length) setAddress(getAddress(accounts[0]!)); else { safeStorage.set(LAST_WALLET_KEY, null); reset(); } }) as (...a: never[]) => void;
    const onChain = ((hex: string) => setChainId(Number(hex))) as (...a: never[]) => void;
    const onDisconnect = (() => undefined) as (...a: never[]) => void; // MetaMask fires this on RPC hiccups: not a user disconnect
    p.on('accountsChanged', onAccounts);
    p.on('chainChanged', onChain);
    p.on('disconnect', onDisconnect);
    return () => { p.removeListener?.('accountsChanged', onAccounts); p.removeListener?.('chainChanged', onChain); p.removeListener?.('disconnect', onDisconnect); };
  }, [active, reset]);

  const client = useMemo(
    () => (active && address ? createWalletClient({ account: address, chain: robinhoodTestnet, transport: custom(active.provider) }) : null),
    [active, address],
  );

  const value = useMemo<WalletContextValue>(() => ({
    wallets, status, address, chainId, client, settled,
    walletLabel: active ? (active.account ? account : active.name) : null,
    walletKind: active?.kind ?? null,
    wrongNetwork: status === 'connected' && chainId !== MARKET.chainId,
    connect,
    disconnect: () => {
      safeStorage.set(LAST_WALLET_KEY, null);
      void active?.disconnect?.().catch(() => undefined); // ends the Privy session; extensions have nothing to end
      reset();
    },
    switchNetwork: async () => { if (active) await switchNetworkOn(active.provider); },
    watchAsset: async (token) => {
      if (!active || active.kind === 'embedded') return;
      await active.provider.request({ method: 'wallet_watchAsset', params: { type: 'ERC20', options: token } });
    },
  }), [wallets, status, address, chainId, client, settled, active, account, connect, reset, switchNetworkOn]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWallet(): WalletContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useWallet outside WalletProvider');
  return v;
}
